import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { AuthLoginRequest } from '@/api/handlers/resources/auth';
import type { AuthSessionResponse } from '@/api/handlers/resources/auth';
import type { ApiResponse } from '@/api/handlers/responses';
import { setSession } from '@/utils/authSession';

/**
 * Login mutation. Persists the returned session on a successful envelope so the
 * bearer interceptor can attach it immediately; failures (any cause — unknown
 * email, wrong password, disabled account) come back as a generic failed
 * envelope the caller surfaces verbatim.
 *
 * A new session starts with an empty query cache. The client outlives
 * sign-out, so without this the next user in the same tab would be served
 * the previous session's reads, and the onboarding gate (whose organisation
 * read never goes stale on its own) would decide from the previous
 * organisation. Queries only, not mutations: this mutation is still settling.
 */
export const useLoginMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<AuthSessionResponse>,
        Error,
        AuthLoginRequest
    >({
        mutationFn: (body) => apiClient.auth.login(body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.removeQueries();
                setSession(result.response);
            }
        },
    });
};
